
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import certificateService from "../services/certificateService";

const EXPIRING_DAYS = 30;

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(`${value}`.slice(0, 10) + "T00:00:00");

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) {
        return { label: "No Expiry Date", className: "cert-status-neutral" };
    }

    const expiry = new Date(`${expiryDate}`.slice(0, 10) + "T00:00:00");
    if (Number.isNaN(expiry.getTime())) {
        return { label: "Unknown", className: "cert-status-neutral" };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysRemaining = Math.ceil(
        (expiry.getTime() - today.getTime()) / 86400000
    );

    if (daysRemaining < 0) {
        return { label: "Expired", className: "cert-status-expired" };
    }

    if (daysRemaining <= EXPIRING_DAYS) {
        return {
            label: "Expiring Soon",
            className: "cert-status-expiring",
        };
    }

    return { label: "Valid", className: "cert-status-valid" };
};

const getTypeName = (certificate, types) => {
    if (certificate.certificate_type?.type_name) {
        return certificate.certificate_type.type_name;
    }

    const type = types.find(
        (item) => Number(item.id) === Number(certificate.certificate_type_id)
    );

    return type?.type_name || "-";
};

const getErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.message ||
    "Unable to load certificates.";

export default function CertificateRegisterListPage() {
    const navigate = useNavigate();

    const [certificates, setCertificates] = useState([]);
    const [certificateTypes, setCertificateTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    const [codeSearch, setCodeSearch] = useState("");
    const [nameSearch, setNameSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [expiryFilter, setExpiryFilter] = useState("ALL");
    const [appliedFilters, setAppliedFilters] = useState({
        code: "",
        name: "",
        type: "",
        expiry: "ALL",
    });

    const [page, setPage] = useState(1);
    const pageSize = 10;

    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError("");

        try {
            const [certificateResponse, typeResponse] = await Promise.all([
                //certificateService.listCertificates(),
                certificateService.listCertificates({ include_inactive: true }),
                certificateService.listCertificateTypes(),
            ]);

            // Supports either an array or a standard rows/data wrapper.
            const certificateRows = Array.isArray(certificateResponse)
                ? certificateResponse
                : certificateResponse?.rows ||
                  certificateResponse?.items ||
                  certificateResponse?.data ||
                  [];

            const typeRows = Array.isArray(typeResponse)
                ? typeResponse
                : typeResponse?.rows ||
                  typeResponse?.items ||
                  typeResponse?.data ||
                  [];

            setCertificates(certificateRows);
            setCertificateTypes(typeRows);
        } catch (error) {
            setLoadError(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const certificateRows = useMemo(
    () =>
        certificates.filter(
            (item) =>
                item.is_deleted !== true &&
                (showInactive || item.is_active !== false)
        ),
    [certificates, showInactive]
);

    const kpis = useMemo(() => {
        const counts = {
            total: certificateRows.length,
            valid: 0,
            expiring: 0,
            expired: 0,
        };

        certificateRows.forEach((certificate) => {
            const status = getExpiryStatus(certificate.expiry_date).label;

            if (status === "Valid") counts.valid += 1;
            if (status === "Expiring Soon") counts.expiring += 1;
            if (status === "Expired") counts.expired += 1;
        });

        return counts;
    }, [certificateRows]);

    const filteredCertificates = useMemo(() => {
        const code = appliedFilters.code.trim().toLowerCase();
        const name = appliedFilters.name.trim().toLowerCase();

        return certificateRows.filter((certificate) => {
            const matchesCode = !code ||
                String(certificate.certificate_code || "")
                    .toLowerCase()
                    .includes(code);

            const matchesName = !name ||
                String(certificate.certificate_name || "")
                    .toLowerCase()
                    .includes(name);

            const matchesType = !appliedFilters.type ||
                Number(certificate.certificate_type_id) ===
                    Number(appliedFilters.type);

            const expiryStatus = getExpiryStatus(certificate.expiry_date).label;

            const matchesExpiry =
                appliedFilters.expiry === "ALL" ||
                (appliedFilters.expiry === "EXPIRED" &&
                    expiryStatus === "Expired") ||
                (appliedFilters.expiry === "EXPIRING" &&
                    expiryStatus === "Expiring Soon") ||
                (appliedFilters.expiry === "VALID" &&
                    expiryStatus === "Valid") ||
                (appliedFilters.expiry === "NO_DATE" &&
                    expiryStatus === "No Expiry Date");

            return matchesCode && matchesName && matchesType && matchesExpiry;
        });
    }, [certificateRows, appliedFilters]);

    useEffect(() => {
        setPage(1);
    }, [appliedFilters]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredCertificates.length / pageSize)
    );

    const visibleCertificates = filteredCertificates.slice(
        (page - 1) * pageSize,
        page * pageSize
    );

    const applySearch = () => {
        setAppliedFilters({
            code: codeSearch,
            name: nameSearch,
            type: typeFilter,
            expiry: expiryFilter,
        });
    };

    const clearSearch = () => {
        setCodeSearch("");
        setNameSearch("");
        setTypeFilter("");
        setExpiryFilter("ALL");
        setAppliedFilters({
            code: "",
            name: "",
            type: "",
            expiry: "ALL",
        });
    };

    const openView = (certificateId) => {
        navigate(
            `/assets/certificate-management/${certificateId}`
        );
    };

    const openEdit = (certificateId) => {
        navigate(
            `/assets/certificate-management/${certificateId}/edit`
        );
    };

    const handleSetActive = async (certificate) => {
    const isCurrentlyActive = certificate.is_active !== false;

    const action = isCurrentlyActive ? "deactivate" : "activate";

    const confirmed = window.confirm(
        `Are you sure you want to ${action} certificate "${certificate.certificate_name}"?`
    );

    if (!confirmed) return;

    setUpdatingStatusId(certificate.id);

    try {
        await certificateService.setCertificateActive(
            certificate.id,
            !isCurrentlyActive
        );

        await loadData();
    } catch (error) {
        window.alert(getErrorMessage(error));
    } finally {
        setUpdatingStatusId(null);
    }
};

    return (
        <div className="container-fluid py-3 certificate-register-page">
            <style>{`
                .certificate-register-page {
                    color: #172033;
                }

                .cert-page-heading {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    flex-wrap: wrap;
                    margin-bottom: 22px;
                }

                .cert-page-title {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    margin: 0;
                    font-size: 27px;
                    font-weight: 750;
                    letter-spacing: -.5px;
                }

                .cert-title-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    background: linear-gradient(135deg, #e0edff, #c7dcff);
                    color: #075bd8;
                    font-size: 25px;
                }

                .cert-subtitle {
                    color: #778196;
                    font-size: 13px;
                    margin-top: 5px;
                }

                .cert-primary-btn {
                    border: 0;
                    border-radius: 10px;
                    background: linear-gradient(135deg, #1769e8, #0647bd);
                    color: #fff;
                    padding: 12px 19px;
                    font-weight: 650;
                    box-shadow: 0 5px 14px rgba(23,105,232,.22);
                }

                .cert-primary-btn:hover {
                    color: #fff;
                    filter: brightness(1.06);
                }

                .cert-kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .cert-kpi-card {
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    min-height: 116px;
                    padding: 20px;
                    border-radius: 15px;
                    color: white;
                    box-shadow: 0 7px 18px rgba(30,55,100,.12);
                }

                .cert-kpi-card::after {
                    content: "";
                    position: absolute;
                    width: 180px;
                    height: 115px;
                    right: -38px;
                    bottom: -72px;
                    border-radius: 50%;
                    background: rgba(255,255,255,.13);
                    transform: rotate(-20deg);
                }

                .cert-kpi-blue {
                    background: linear-gradient(125deg, #20a4f3 0%, #0860e8 72%, #0747c8 100%);
                }

                .cert-kpi-purple {
                    background: linear-gradient(125deg, #a78bfa 0%, #8250ed 58%, #6934d8 100%);
                }

                .cert-kpi-orange {
                    background: linear-gradient(125deg, #ffbd27 0%, #ff8a00 58%, #f46a00 100%);
                }

                .cert-kpi-red {
                    background: linear-gradient(125deg, #ff9292 0%, #f34f73 58%, #df2859 100%);
                }

                .cert-kpi-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex: 0 0 58px;
                    width: 58px;
                    height: 58px;
                    border-radius: 50%;
                    background: rgba(255,255,255,.18);
                    font-size: 27px;
                }

                .cert-kpi-label {
                    font-size: 13px;
                    font-weight: 650;
                    opacity: .95;
                    margin-bottom: 7px;
                }

                .cert-kpi-value {
                    font-size: 31px;
                    font-weight: 800;
                    line-height: 1;
                }

                .cert-panel {
                    background: #fff;
                    border: 1px solid #e6eaf1;
                    border-radius: 13px;
                    padding: 20px;
                    margin-bottom: 17px;
                    box-shadow: 0 3px 12px rgba(21,37,68,.045);
                }

                .cert-filter-grid {
                    display: grid;
                    grid-template-columns: 1.15fr 1.15fr 1fr .85fr auto auto;
                    align-items: end;
                    gap: 13px;
                }

                .cert-field label {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 13px;
                    font-weight: 700;
                    color: #253047;
                }

                .cert-field input,
                .cert-field select {
                    width: 100%;
                    height: 43px;
                    border: 1px solid #cfd6e2;
                    border-radius: 8px;
                    padding: 0 12px;
                    background: #fff;
                    color: #29354b;
                    outline: none;
                }

                .cert-field input:focus,
                .cert-field select:focus {
                    border-color: #3980f5;
                    box-shadow: 0 0 0 3px rgba(57,128,245,.12);
                }

                .cert-search-btn,
                .cert-clear-btn {
                    height: 43px;
                    border-radius: 8px;
                    padding: 0 17px;
                    font-weight: 650;
                    white-space: nowrap;
                }

                .cert-search-btn {
                    background: linear-gradient(135deg, #1769e8, #0647bd);
                    border: 1px solid #0758d5;
                    color: #fff;
                }

                .cert-clear-btn {
                    background: #fff;
                    border: 1px solid #cbd3df;
                    color: #263247;
                }

                .cert-table-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    flex-wrap: wrap;
                    margin-bottom: 15px;
                    color: #47536a;
                    font-size: 13px;
                }

                .cert-table-wrap {
                    width: 100%;
                    overflow-x: auto;
                }

                .cert-table {
                    width: 100%;
                    min-width: 970px;
                    border-collapse: separate;
                    border-spacing: 0;
                }

                .cert-table th {
                    padding: 13px 12px;
                    text-align: left;
                    background: #f1f4fa;
                    color: #29354a;
                    font-size: 12px;
                    font-weight: 750;
                    border-top: 1px solid #e2e7f0;
                    border-bottom: 1px solid #e2e7f0;
                    white-space: nowrap;
                }

                .cert-table th:first-child {
                    border-top-left-radius: 8px;
                }

                .cert-table th:last-child {
                    border-top-right-radius: 8px;
                }

                .cert-table td {
                    padding: 13px 12px;
                    border-bottom: 1px solid #edf0f5;
                    font-size: 13px;
                    color: #29354a;
                    white-space: nowrap;
                }

                .cert-table tbody tr:hover td {
                    background: #f8faff;
                }

                .cert-code {
                    color: #145ac7 !important;
                    font-weight: 700;
                }

                .cert-status {
                    display: inline-flex;
                    align-items: center;
                    border-radius: 30px;
                    padding: 5px 10px;
                    font-size: 11px;
                    font-weight: 750;
                }

                .cert-status-valid {
                    color: #137746;
                    background: #d9f7e7;
                }

                .cert-status-expiring {
                    color: #a75b00;
                    background: #fff0ce;
                }

                .cert-status-expired {
                    color: #bd2531;
                    background: #ffe0e2;
                }

                .cert-status-neutral {
                    color: #596579;
                    background: #e9edf3;
                }

                .cert-actions {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                }

                .cert-icon-btn {
                    width: 30px;
                    height: 30px;
                    border: 0;
                    border-radius: 7px;
                    background: transparent;
                    color: #2457a7;
                    font-size: 16px;
                }

                .cert-icon-btn:hover {
                    background: #eaf1ff;
                }

                .cert-icon-delete {
                    color: #dc3545;
                }

                .cert-empty {
                    text-align: center;
                    padding: 45px 15px !important;
                    color: #778196 !important;
                }

                .cert-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    flex-wrap: wrap;
                    margin-top: 18px;
                }

                .cert-page-buttons {
                    display: flex;
                    gap: 5px;
                }

                .cert-page-buttons button {
                    min-width: 35px;
                    height: 35px;
                    border: 1px solid #dce2ec;
                    border-radius: 7px;
                    background: #fff;
                    color: #33415a;
                }

                .cert-page-buttons button.active {
                    background: #075bd8;
                    border-color: #075bd8;
                    color: #fff;
                }

                .cert-page-buttons button:disabled {
                    opacity: .45;
                    cursor: not-allowed;
                }

                .cert-alert {
                    border-radius: 9px;
                    padding: 12px 15px;
                    margin-bottom: 15px;
                }

                @media (max-width: 1100px) {
                    .cert-kpi-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .cert-filter-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }

                @media (max-width: 576px) {
                    .cert-kpi-grid,
                    .cert-filter-grid {
                        grid-template-columns: 1fr;
                    }

                    .cert-page-title {
                        font-size: 22px;
                    }

                    .cert-panel {
                        padding: 13px;
                    }
                }
            `}</style>

            <div className="cert-page-heading">
                <div>
                    <div className="text-muted small mb-2">
                        Assets <span className="mx-2">›</span>
                        Certificate Management <span className="mx-2">›</span>
                        <strong>Certificate Register</strong>
                    </div>

                    <h2 className="cert-page-title">
                        <span className="cert-title-icon">♙</span>
                        Certificate Register
                    </h2>

                    <div className="cert-subtitle">
                        Manage certificates, expiry dates and renewals.
                    </div>
                </div>

                <button
                    type="button"
                    className="cert-primary-btn"
                    onClick={() =>
                        navigate("/assets/certificate-management/new")
                    }
                >
                    <span className="me-2">＋</span>
                    Add Certificate
                </button>
            </div>

            <div className="cert-kpi-grid">
                <div className="cert-kpi-card cert-kpi-blue">
                    <div className="cert-kpi-icon">▤</div>
                    <div>
                        <div className="cert-kpi-label">Total Certificates</div>
                        <div className="cert-kpi-value">{kpis.total}</div>
                    </div>
                </div>

                <div className="cert-kpi-card cert-kpi-purple">
                    <div className="cert-kpi-icon">✓</div>
                    <div>
                        <div className="cert-kpi-label">Valid</div>
                        <div className="cert-kpi-value">{kpis.valid}</div>
                    </div>
                </div>

                <div className="cert-kpi-card cert-kpi-orange">
                    <div className="cert-kpi-icon">⌛</div>
                    <div>
                        <div className="cert-kpi-label">
                            Expiring Soon (≤ 30 Days)
                        </div>
                        <div className="cert-kpi-value">{kpis.expiring}</div>
                    </div>
                </div>

                <div className="cert-kpi-card cert-kpi-red">
                    <div className="cert-kpi-icon">⊗</div>
                    <div>
                        <div className="cert-kpi-label">Expired</div>
                        <div className="cert-kpi-value">{kpis.expired}</div>
                    </div>
                </div>
            </div>

            <div className="cert-panel">
                <div className="cert-filter-grid">
                    <div className="cert-field">
                        <label htmlFor="cert-code-search">Certificate Code</label>
                        <input
                            id="cert-code-search"
                            value={codeSearch}
                            onChange={(event) => setCodeSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") applySearch();
                            }}
                            placeholder="Search code..."
                        />
                    </div>

                    <div className="cert-field">
                        <label htmlFor="cert-name-search">Certificate Name</label>
                        <input
                            id="cert-name-search"
                            value={nameSearch}
                            onChange={(event) => setNameSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") applySearch();
                            }}
                            placeholder="Search name..."
                        />
                    </div>

                    <div className="cert-field">
                        <label htmlFor="cert-type-filter">Certificate Type</label>
                        <select
                            id="cert-type-filter"
                            value={typeFilter}
                            onChange={(event) => setTypeFilter(event.target.value)}
                        >
                            <option value="">All Types</option>
                            {certificateTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.type_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="cert-field">
                        <label htmlFor="cert-expiry-filter">Expiry</label>
                        <select
                            id="cert-expiry-filter"
                            value={expiryFilter}
                            onChange={(event) => setExpiryFilter(event.target.value)}
                        >
                            <option value="ALL">All</option>
                            <option value="VALID">Valid</option>
                            <option value="EXPIRING">Expiring Soon</option>
                            <option value="EXPIRED">Expired</option>
                            <option value="NO_DATE">No Expiry Date</option>
                        </select>
                    </div>

                    <button
                        type="button"
                        className="cert-search-btn"
                        onClick={applySearch}
                    >
                        <span className="me-1">⌕</span> Search
                    </button>

                    <button
                        type="button"
                        className="cert-clear-btn"
                        onClick={clearSearch}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {loadError && (
                <div className="alert alert-danger cert-alert" role="alert">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                        <span>{loadError}</span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={loadData}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            <div className="cert-panel">
                <div className="cert-table-top">
                    <span>
                        {loading
                            ? "Loading certificates..."
                            : `Showing ${
                                  filteredCertificates.length
                                      ? (page - 1) * pageSize + 1
                                      : 0
                              } – ${Math.min(
                                  page * pageSize,
                                  filteredCertificates.length
                              )} of ${filteredCertificates.length} records`}
                    </span>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <label className="d-flex align-items-center gap-2 small mb-0">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(event) => {
                                    setShowInactive(event.target.checked);
                                    setPage(1);
                                }}
                            />
                            Show Inactive
                        </label>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={loadData}
                            disabled={loading}
                        >
                            ↻ Refresh
                        </button>
                    </div>
                </div>

                <div className="cert-table-wrap">
                    <table className="cert-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Certificate Code</th>
                                <th>Certificate Name</th>
                                <th>Type</th>
                                <th>Branch / Location</th>
                                <th>Issue Date</th>
                                <th>Expiry Date</th>
                                <th>Status</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="cert-empty">
                                        <div
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        />
                                        Loading certificate records...
                                    </td>
                                </tr>
                            ) : visibleCertificates.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="cert-empty">
                                        <div className="fs-4 mb-2">▤</div>
                                        <strong>No certificates found</strong>
                                        <div className="small mt-1">
                                            Try changing your filters or add a certificate.
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                visibleCertificates.map((certificate, index) => {
                                    const status = getExpiryStatus(
                                        certificate.expiry_date
                                    );

                                    return (
                                        <tr key={certificate.id}>
                                            <td>
                                                {(page - 1) * pageSize + index + 1}
                                            </td>
                                            <td className="cert-code">
                                                {certificate.certificate_code || "-"}
                                            </td>
                                            <td>
                                                <strong>
                                                    {certificate.certificate_name || "-"}
                                                </strong>
                                            </td>
                                            <td>
                                                {getTypeName(
                                                    certificate,
                                                    certificateTypes
                                                )}
                                            </td>
                                            <td>{certificate.branch || "-"}</td>
                                            <td>{formatDate(certificate.issue_date)}</td>
                                            <td>{formatDate(certificate.expiry_date)}</td>
                                            <td>
                                                <span
                                                    className={`cert-status ${status.className}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="cert-actions">
                                                    <button
                                                        type="button"
                                                        className="cert-icon-btn"
                                                        title="View certificate"
                                                        aria-label="View certificate"
                                                        onClick={() => openView(certificate.id)}
                                                    >
                                                        ◉
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cert-icon-btn"
                                                        title="Edit certificate"
                                                        aria-label="Edit certificate"
                                                        onClick={() => openEdit(certificate.id)}
                                                    >
                                                        ✎
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cert-icon-btn"
                                                        title={
                                                            certificate.is_active === false
                                                                ? "Activate certificate"
                                                                : "Deactivate certificate"
                                                        }
                                                        aria-label={
                                                            certificate.is_active === false
                                                                ? "Activate certificate"
                                                                : "Deactivate certificate"
                                                        }
                                                        disabled={updatingStatusId === certificate.id}
                                                        onClick={() => handleSetActive(certificate)}
                                                    >
                                                        {updatingStatusId === certificate.id
                                                            ? "…"
                                                            : certificate.is_active === false
                                                            ? "✓"
                                                            : "⊘"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && filteredCertificates.length > 0 && (
                    <div className="cert-pagination">
                        <span className="text-muted small">
                            Page {page} of {totalPages}
                        </span>

                        <div className="cert-page-buttons">
                            <button
                                type="button"
                                title="First page"
                                disabled={page === 1}
                                onClick={() => setPage(1)}
                            >
                                «
                            </button>

                            <button
                                type="button"
                                title="Previous page"
                                disabled={page === 1}
                                onClick={() => setPage((current) => current - 1)}
                            >
                                ‹
                            </button>

                            {Array.from(
                                { length: totalPages },
                                (_, index) => index + 1
                            )
                                .filter(
                                    (pageNumber) =>
                                        pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        Math.abs(pageNumber - page) <= 1
                                )
                                .map((pageNumber, index, pages) => (
                                    <React.Fragment key={pageNumber}>
                                        {index > 0 &&
                                            pageNumber - pages[index - 1] > 1 && (
                                                <span className="px-1 align-self-center">
                                                    …
                                                </span>
                                            )}
                                        <button
                                            type="button"
                                            className={
                                                pageNumber === page ? "active" : ""
                                            }
                                            onClick={() => setPage(pageNumber)}
                                        >
                                            {pageNumber}
                                        </button>
                                    </React.Fragment>
                                ))}

                            <button
                                type="button"
                                title="Next page"
                                disabled={page === totalPages}
                                onClick={() => setPage((current) => current + 1)}
                            >
                                ›
                            </button>

                            <button
                                type="button"
                                title="Last page"
                                disabled={page === totalPages}
                                onClick={() => setPage(totalPages)}
                            >
                                »
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
